// src/ActivityForm.js
//
// Same shared create/edit pattern as ProjectForm.js. NEW CONCEPT here:
// Activity belongs to a Project, so this form needs a DROPDOWN of
// existing projects to choose from - which means it must fetch the
// project list itself before it can render that dropdown.

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function ActivityForm({ token, activity, onSaved, onCancel }) {
  // projectOptions holds the list of projects to populate the <select>.
  // Starts empty and gets filled by the useEffect below.
  const [projectOptions, setProjectOptions] = useState([]);

  const [project, setProject] = useState(activity?.project || '');
  const [name, setName] = useState(activity?.name || '');
  const [description, setDescription] = useState(activity?.description || '');
  const [plannedDate, setPlannedDate] = useState(activity?.planned_date || '');
  const [status, setStatus] = useState(activity?.status || 'planned');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch the list of projects ONCE, when the form first mounts, so we
  // have something to show in the dropdown. Empty dependency array []
  // means "run once on mount", not tied to any changing value.
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/projects/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setProjectOptions(data);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      project,          // the project ID selected in the dropdown
      name,
      description,
      planned_date: plannedDate,
      status,
    };

    const url = activity
      ? `${API_BASE_URL}/api/activities/${activity.id}/`
      : `${API_BASE_URL}/api/activities/`;
    const method = activity ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        setError(formatApiError(await response.json()));
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">{activity ? 'Edit Activity' : 'New Activity'}</h2>
      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
        <select
          value={project}
          onChange={(e) => setProject(e.target.value)}
          required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          {/* A disabled placeholder option, so the dropdown doesn't
              silently default to the first real project in the list */}
          <option value="" disabled>Select a project...</option>
          {projectOptions.map((p) => (
            // value={p.id} is what actually gets SENT to the backend.
            // The text between the tags is only what the human sees.
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3"
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Planned Date</label>
        <input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="planned">Planned</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>

        {error && <p className="text-red-500 text-sm mb-4 break-words">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ActivityForm;
